/**
 * useTagShowcases Hook
 * Fetches personalized tag-based showcases with infinite scroll pagination
 * Each showcase is one tag with its matching listings
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { PAGE_SIZE } from '../data/js/pagination';
import { parseJsonResponse } from '../utils/read-json-response';
import { appendPageLoadAt, appendShuffleSeed } from '../utils/seeded-shuffle';
import { errorMessageOr } from '../utils/error-message';

/**
 * One page from /api/recommendations/tag-showcases, success or failure. A
 * success always carries its rows and `hasMore` (TagShowcasesSuccessResponseSchema).
 */
type TagShowcasesPage =
  | { success: true; showcases: TagShowcase[]; hasMore: boolean }
  | { success?: false; error?: string };

interface TagShowcase {
  tag: string;
  title?: string;
  viewCount: number;
  listings: Record<string, unknown>[];
  /** The group the server held the row to; the row's later pages stay in it. */
  categoryGroupId?: number;
}

interface UseTagShowcasesOptions {
  type: 'user' | 'session';
  userId?: number;
  sessionId?: string;
  anonymousId?: string;
  days?: number;
  listingsPerTag?: number;
  minListingsPerTag?: number;
  enabled?: boolean;
}

interface UseTagShowcasesReturn {
  showcases: TagShowcase[];
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  /**
   * True once the FIRST fetch has settled, success or failure. `isLoading`
   * alone cannot say that: it is false both before the fetch starts and after
   * it ends, and a page deciding whether to hold its skeleton needs to tell
   * those two apart.
   */
  hasLoaded: boolean;
  /**
   * How many rows the first page produced, frozen when it arrived. The For You
   * page keeps that many above its global rows and appends every later page
   * below them, so rows already painted never move.
   */
  firstPageCount: number;
  error: string | null;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
}

export function useTagShowcases(options: UseTagShowcasesOptions): UseTagShowcasesReturn {
  const [showcases, setShowcases] = useState<TagShowcase[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [firstPageCount, setFirstPageCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const pageSize = PAGE_SIZE.TAG_SHOWCASE;

  // Derive seenTags from accumulated showcases for dedup across pages
  const seenTags = useMemo(
    () => showcases.map(s => s.tag),
    [showcases]
  );

  const buildParams = useCallback((currentOffset: number, currentSeenTags: string[]) => {
    const params: Record<string, string> = {
      type: options.type,
      offset: String(currentOffset),
      limit: String(pageSize),
    };
    if (options.userId) params.userId = String(options.userId);
    if (options.sessionId) params.sessionId = options.sessionId;
    if (options.anonymousId) params.anonymousId = options.anonymousId;
    if (options.days) params.days = String(options.days);
    if (options.listingsPerTag) params.listingsPerTag = String(options.listingsPerTag);
    if (options.minListingsPerTag) params.minListingsPerTag = String(options.minListingsPerTag);
    const query = new URLSearchParams(params);
    // Only send seenTags on subsequent pages (not initial fetch). One param per
    // tag, never comma-joined: "Dallas, TX" would split in two and its row
    // would come back on the next page.
    if (currentOffset > 0) currentSeenTags.forEach(tag => query.append('seenTags', tag));
    return query;
  }, [options.type, options.userId, options.sessionId, options.anonymousId, options.days, options.listingsPerTag, options.minListingsPerTag, pageSize]);

  const fetchShowcases = useCallback(async () => {
    const params = buildParams(0, []);

    setIsLoading(true);
    setError(null);
    setOffset(0);

    try {
      const response = await fetch(`/api/recommendations/tag-showcases?${appendPageLoadAt(appendShuffleSeed(params))}`);
      const data = await parseJsonResponse<TagShowcasesPage>(
        response,
        'GET /api/recommendations/tag-showcases'
      );

      if (!response.ok || !data.success) {
        throw new Error(('error' in data && data.error) || 'Failed to fetch tag showcases');
      }

      setShowcases(data.showcases);
      setFirstPageCount(data.showcases.length);
      setHasMore(data.hasMore);
    } catch (err) {
      const errorMessage = errorMessageOr(err, 'Unknown error');
      setError(errorMessage);
      console.error('[useTagShowcases] Error:', err);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [buildParams]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    const newOffset = offset + pageSize;
    const params = buildParams(newOffset, seenTags);

    setIsLoadingMore(true);

    try {
      const response = await fetch(`/api/recommendations/tag-showcases?${appendPageLoadAt(appendShuffleSeed(params))}`);
      const data = await parseJsonResponse<TagShowcasesPage>(
        response,
        'GET /api/recommendations/tag-showcases'
      );

      if (!response.ok || !data.success) {
        throw new Error(('error' in data && data.error) || 'Failed to fetch more tag showcases');
      }

      setShowcases(prev => [...prev, ...data.showcases]);
      setHasMore(data.hasMore);
      setOffset(newOffset);
    } catch (err) {
      const errorMessage = errorMessageOr(err, 'Unknown error');
      setError(errorMessage);
      console.error('[useTagShowcases] Error loading more:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, offset, pageSize, buildParams, seenTags]);

  useEffect(() => {
    if (options.enabled !== false) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- fetches the first page of tag showcases when the hook's inputs change: the setState calls are the request's own loading/error/result flags inside the async fetchShowcases; the rule follows the call but cannot see the await boundary
      fetchShowcases();
    }
  }, [
    options.type,
    options.userId,
    options.sessionId,
    options.anonymousId,
    options.days,
    options.listingsPerTag,
    options.minListingsPerTag,
    options.enabled,
  ]);

  return {
    showcases,
    hasMore,
    isLoading,
    isLoadingMore,
    hasLoaded,
    firstPageCount,
    error,
    loadMore,
    refetch: fetchShowcases,
  };
}
